import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as css from './AiAgentUserMessage.css';
import AiAgentUserMessageEditActions from './AiAgentUserMessageEditActions';
import resizeTextareaToContent from '../../../../library/resizeTextareaToContent';

interface AiAgentUserMessageEditProps {
    initialText: string;
    index: number;
    onSave: (text: string) => void;
    onCancel: () => void;
}

function AiAgentUserMessageEdit({initialText, index, onSave, onCancel}: AiAgentUserMessageEditProps) {
    const [editText, setEditText] = useState(initialText);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = textareaRef.current;
        resizeTextareaToContent(el);
        if(!el) {
            return;
        }
        el.focus({preventScroll: true});
        el.setSelectionRange(el.value.length, el.value.length);
    }, []);

    const handleSave = useCallback(() => {
        const trimmed = editText.trim();
        if(!trimmed) {
            onCancel();
            return;
        }
        onSave(trimmed);
    }, [editText, onSave, onCancel]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditText(event.target.value);
        resizeTextareaToContent(event.target);
    }, []);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSave();
            return;
        }
        if(event.key === 'Escape') {
            event.preventDefault();
            onCancel();
        }
    }, [handleSave, onCancel]);

    return (
        <css.editingBubble data-cy={`ai-agent-user-message-edit-form-${index}`}>
            <css.editField
                ref={textareaRef}
                value={editText}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                rows={1}
                data-cy={`ai-agent-user-message-edit-field-${index}`}
            />
            <css.editDivider />
            <AiAgentUserMessageEditActions index={index} onSave={handleSave} onCancel={onCancel} />
        </css.editingBubble>
    );
}

export default AiAgentUserMessageEdit;
